import {Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, FormControlLabel, IconButton, Radio, RadioGroup, TextField, Tooltip} from '@mui/material'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import TuneIcon from '@mui/icons-material/Tune'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined'
import {CSS} from '@dnd-kit/utilities'
import {useSortable} from '@dnd-kit/sortable'
import React, {ChangeEvent, DragEvent, useEffect, useMemo, useState} from 'react'
import styled from 'styled-components'
import {Step, StepContent, StepLabel} from '@mui/material'
import {v4 as uuidv4} from 'uuid'
import {FlowDocumentType, QuestionNode} from '../../types/flowData'
import {DocumentTypeBadge} from '../../constants/documentTypes'

// --- Criterio types ---

type CriterioItem = {
    id: string
    question_uuid: string
    values: string[]
}

type CriterioOrGroup = {
    id: string
    OR: CriterioItem[]
}

type CriterioAndItem = CriterioItem | CriterioOrGroup

export type RuleStructure = {
    AND: CriterioAndItem[]
}

export type MinutaData = {
    id: string
    templateName: string
    tipoDocumento: string
    minutaFileName: string | null
    minutaFile: File | null
    minutaContentFileName: string | null
    minutaContentFile: File | null
    prompt: string
    criterios: RuleStructure
    timbradoFileName: string | null
    timbradoFile: File | null
    forceExtraction: boolean
    minutaFileKey: number
    minutaContentFileKey: number
    timbradoFileKey: number
}

type MinutaStepProps = {
    item: MinutaData
    index: number
    onUpdate: (id: string, patch: Partial<MinutaData>) => void
    onRemove: (id: string) => void
    onMinutaFileChange: (id: string, file: File | null) => void
    onMinutaFileRemove: (id: string) => void
    onTemplateFileChange: (id: string, file: File | null) => void
    onTemplateFileRemove: (id: string) => void
    onTimbradoFileChange: (id: string, file: File | null) => void
    onTimbradoFileRemove: (id: string) => void
    flowQuestions?: QuestionNode[]
    fixedMinutas?: boolean
    documentTypes?: FlowDocumentType[]
    // minimizar/maximizar todas; cada minuta ainda pode alternar sozinha
    collapseAll?: boolean
    // Gera um projeto a partir desta minuta. `file` null = responder pelo
    // draft.docx; com arquivo = enviar ele na pergunta de upload do fluxo.
    // Ausente = botão escondido.
    onGenerateProject?: (
        id: string,
        file: File | null,
        answerInstruction: string
    ) => Promise<void> | void
    // Ajusta o draft.json desta minuta: instrução em texto, um documento gerado
    // para comparar com a minuta, ou os dois.
    onAdjustManifest?: (
        id: string,
        instruction: string,
        generatedDocument: File | null
    ) => Promise<void> | void
}

// --- Helpers ---

const isOrGroup = (item: CriterioAndItem): item is CriterioOrGroup =>
    'OR' in item

const newCriterioItem = (): CriterioItem => ({
    id: uuidv4(),
    question_uuid: '',
    values: [],
})

export const createDefaultCriterios = (): RuleStructure => ({
    AND: [newCriterioItem()],
})


// --- Styled components ---

const Card = styled.div`
    border: 1px solid #d0d7de;
    border-radius: 8px;
    background-color: #ffffff;
    padding: 24px;
    display: flex;
    flex-direction: column;
    gap: 12px;
`

const QuestionTitle = styled.label`
    font-family: Roboto, sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 21px;
    letter-spacing: 0px;
    color: #1f2328;
`

const QuestionSubtitle = styled.p`
    font-family: Roboto, sans-serif;
    font-weight: 400;
    font-size: 13px;
    line-height: 19.5px;
    letter-spacing: 0px;
    color: #838383;
    margin: 0;
`

const Select = styled.select`
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #d0d7de;
    border-radius: 6px;
    background-color: #ffffff;
    color: #24292f;
    font-size: 14px;
    outline: none;
`

const TextArea = styled.textarea`
    width: 100%;
    min-height: 120px;
    padding: 12px;
    border: 1px solid #d0d7de;
    border-radius: 8px;
    font-size: 14px;
    color: #24292f;
    resize: vertical;
`

const UploadInput = styled.input`
    width: 100%;
    padding: 10px 12px;
    border: 1px solid #d0d7de;
    border-radius: 6px;
    background-color: #ffffff;
    font-size: 14px;
    color: #24292f;
`

const SelectedFile = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 12px;
    border-radius: 6px;
    background-color: #f6f8fa;
    color: #24292f;
    font-size: 14px;
`

const SelectedFileInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`

const RemoveFileButton = styled.button`
    border: none;
    background: transparent;
    color: #d1242f;
    font-size: 14px;
    cursor: pointer;
    padding: 0;
`

const Divider = styled.hr`
    border: none;
    border-top: 1px solid #d0d7de;
    margin: 12px 25px;
`

const RadioBox = styled.div`
    background-color: #f6f8fa;
    border-radius: 8px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
`

const RadioOption = styled.label`
    display: flex;
    align-items: center;
    gap: 8px;
    font-family: Roboto, sans-serif;
    font-weight: 400;
    font-size: 13px;
    line-height: 19.5px;
    color: #24292f;
`

const DropZone = styled.label`
    border: 1px dashed #d0d7de;
    border-radius: 8px;
    padding: 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 6px;
    background-color: #ffffff;
    cursor: pointer;
`

const DropZoneText = styled.span`
    font-family: Roboto, sans-serif;
    font-weight: 500;
    font-size: 14px;
    line-height: 21px;
    color: #24292f;
`

const DropZoneHint = styled.span`
    font-family: Roboto, sans-serif;
    font-weight: 400;
    font-size: 13px;
    line-height: 19.5px;
    color: #838383;
`

const HiddenInput = styled.input`
    display: none;
`

const HeaderRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
`

const HeaderLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`

const HeaderRight = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
`

const ChevronIcon = styled(ExpandMoreIcon)<{$collapsed: boolean}>`
    transition: transform 0.2s ease;
    transform: ${({$collapsed}) => ($collapsed ? 'rotate(-90deg)' : 'rotate(0deg)')};
    color: #57606a;
`

// Criterio styled components

const CriterioWrapper = styled.div`
    display: flex;
    flex-direction: column;
    gap: 4px;
`

const CriterioBlockCard = styled.div`
    border: 1px solid #d0d7de;
    border-radius: 8px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    background: #ffffff;
`

const CriterioBlockHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
`

const BlockLabel = styled.span`
    font-family: Roboto, sans-serif;
    font-size: 12px;
    font-weight: 500;
    color: #57606a;
`

const ConnectorRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 4px 0;
`

const ConnectorLine = styled.div`
    flex: 1;
    height: 1px;
    background: #d0d7de;
`

const ConnectorPill = styled.span<{
    variant: 'and' | 'or'
    clickable?: boolean
}>`
    font-family: Roboto, sans-serif;
    font-size: 11px;
    font-weight: 600;
    color: ${({variant}) => (variant === 'or' ? '#0969da' : '#57606a')};
    text-transform: uppercase;
    letter-spacing: 0.5px;
    padding: 2px 8px;
    background: ${({variant}) => (variant === 'or' ? '#ddf4ff' : '#f6f8fa')};
    border: 1px solid
        ${({variant}) => (variant === 'or' ? '#54aeff' : '#d0d7de')};
    border-radius: 10px;
    cursor: ${({clickable}) => (clickable ? 'pointer' : 'default')};
    user-select: none;
    ${({clickable, variant}) =>
        clickable &&
        `&:hover {
            background: ${variant === 'or' ? '#b6e3ff' : '#eaeef2'};
        }`}
`

const OrGroupCard = styled.div`
    border: 1px solid #54aeff;
    border-radius: 8px;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    background: #f0f8ff;
`

const OrGroupHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 4px;
`

const CriterioActions = styled.div`
    display: flex;
    gap: 8px;
    margin-top: 4px;
`

// --- Component ---

export default function MinutaStep({
    item,
    index,
    onUpdate,
    onRemove,
    onMinutaFileChange,
    onMinutaFileRemove,
    onTemplateFileChange,
    onTemplateFileRemove,
    onTimbradoFileChange,
    onTimbradoFileRemove,
    flowQuestions = [],
    fixedMinutas = false,
    documentTypes = [],
    collapseAll = false,
    onGenerateProject,
    onAdjustManifest,
}: MinutaStepProps) {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging,
    } = useSortable({id: item.id})

    const [collapsed, setCollapsed] = useState(collapseAll)
    useEffect(() => setCollapsed(collapseAll), [collapseAll])
    const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)
    const [generating, setGenerating] = useState(false)
    const [generateOpen, setGenerateOpen] = useState(false)
    const [generateMode, setGenerateMode] = useState<'draft' | 'upload'>('draft')
    const [generateFile, setGenerateFile] = useState<File | null>(null)
    const [generateInstruction, setGenerateInstruction] = useState('')
    const [adjustOpen, setAdjustOpen] = useState(false)
    const [adjustInstruction, setAdjustInstruction] = useState('')
    const [adjustFile, setAdjustFile] = useState<File | null>(null)
    const [adjusting, setAdjusting] = useState(false)

    // Sem tipo de documento não há como achar o draft no S3 nem criar o projeto.
    const acoesHabilitadas = Boolean(item.tipoDocumento)

    const handleGenerateProject = async () => {
        if (!onGenerateProject || generating) return
        setGenerating(true)
        try {
            await onGenerateProject(
                item.id,
                generateMode === 'upload' ? generateFile : null,
                generateInstruction.trim()
            )
            setGenerateOpen(false)
            setGenerateFile(null)
            setGenerateInstruction('')
            setGenerateMode('draft')
        } finally {
            setGenerating(false)
        }
    }

    const handleAdjustSubmit = async () => {
        if (!onAdjustManifest || adjusting) return
        setAdjusting(true)
        try {
            await onAdjustManifest(item.id, adjustInstruction.trim(), adjustFile)
            setAdjustOpen(false)
            setAdjustInstruction('')
            setAdjustFile(null)
        } finally {
            setAdjusting(false)
        }
    }

    const questions = useMemo(
        () =>
            flowQuestions
                .filter((node) =>
                    ['select', 'multiselect'].includes(
                        node.question_data?.question_type
                    )
                )
                .map((node) => {
                    const q = node.question_data
                    const opts =
                        (q.type as any)?.select?.options ??
                        (q.type as any)?.multiselect?.options ??
                        []
                    return {
                        id: q.id,
                        label: q.content ,
                        options: (opts as {title: string}[]).map((o) => ({
                            id: o.title,
                            label: o.title,
                        })),
                    }
                }),
        [flowQuestions]
    )

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.6 : 1,
    }

    const apiDocType = documentTypes.find((docType) => docType.id === item.tipoDocumento)
    const selectedDocType =
        apiDocType?.accent && apiDocType.soft
            ? {short: apiDocType.acronym.toUpperCase(), accent: apiDocType.accent, soft: apiDocType.soft}
            : null

    console.log('selectedDocType', selectedDocType, item.tipoDocumento)

    // Existing handlers

    const handleTemplateNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        const slug = event.target.value
            .normalize('NFD')
            .replace(/[̀-ͯ]/g, '')
            .toLowerCase()
            .replace(/[^a-z0-9\s-]/g, '')
            .replace(/\s+/g, '-')
            .replace(/-+/g, '-')
        onUpdate(item.id, {templateName: slug})
    }

    const handleTipoChange = (event: ChangeEvent<HTMLSelectElement>) => {
        onUpdate(item.id, {tipoDocumento: event.target.value})
    }

    const handlePromptChange = (event: ChangeEvent<HTMLTextAreaElement>) => {
        onUpdate(item.id, {prompt: event.target.value})
    }

    const handleMinutaFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] ?? null
        onMinutaFileChange(item.id, file)
    }

    const handleTemplateFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] ?? null
        onTemplateFileChange(item.id, file)
    }

    const handleTimbradoFileChange = (event: ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0] ?? null
        onTimbradoFileChange(item.id, file)
    }

    const handleTimbradoDrop = (event: DragEvent<HTMLLabelElement>) => {
        event.preventDefault()
        const file = event.dataTransfer.files?.[0] ?? null
        onTimbradoFileChange(item.id, file)
    }

    // Criterio handlers

    const updateCriterios = (AND: CriterioAndItem[]) => {
        onUpdate(item.id, {criterios: {AND}})
    }

    const handleAddAndBlock = () => {
        updateCriterios([...item.criterios.AND, newCriterioItem()])
    }

    const handleAddOrGroup = () => {
        const newGroup: CriterioOrGroup = {
            id: uuidv4(),
            OR: [newCriterioItem(), newCriterioItem()],
        }
        updateCriterios([...item.criterios.AND, newGroup])
    }

    const handleRemoveAndItem = (andItemId: string) => {
        updateCriterios(
            item.criterios.AND.filter((a) => a.id !== andItemId)
        )
    }

    const handleUpdateAndBlock = (
        blockId: string,
        patch: Partial<CriterioItem>
    ) => {
        updateCriterios(
            item.criterios.AND.map((a) =>
                !isOrGroup(a) && a.id === blockId ? {...a, ...patch} : a
            )
        )
    }

    const handleAddOrBlock = (groupId: string) => {
        updateCriterios(
            item.criterios.AND.map((a) =>
                isOrGroup(a) && a.id === groupId
                    ? {...a, OR: [...a.OR, newCriterioItem()]}
                    : a
            )
        )
    }

    const handleRemoveOrBlock = (groupId: string, blockId: string) => {
        updateCriterios(
            item.criterios.AND.map((a) =>
                isOrGroup(a) && a.id === groupId
                    ? {...a, OR: a.OR.filter((o) => o.id !== blockId)}
                    : a
            )
        )
    }

    const handleUpdateOrBlock = (
        groupId: string,
        blockId: string,
        patch: Partial<CriterioItem>
    ) => {
        updateCriterios(
            item.criterios.AND.map((a) =>
                isOrGroup(a) && a.id === groupId
                    ? {
                          ...a,
                          OR: a.OR.map((o) =>
                              o.id === blockId ? {...o, ...patch} : o
                          ),
                      }
                    : a
            )
        )
    }

    const handleToggleAndToOr = (andIndex: number) => {
        const prev = item.criterios.AND[andIndex - 1]
        const curr = item.criterios.AND[andIndex]
        if (isOrGroup(prev) || isOrGroup(curr)) return
        const newGroup: CriterioOrGroup = {
            id: uuidv4(),
            OR: [prev, curr],
        }
        updateCriterios([
            ...item.criterios.AND.slice(0, andIndex - 1),
            newGroup,
            ...item.criterios.AND.slice(andIndex + 1),
        ])
    }

    const handleToggleOrToAnd = (groupId: string, orIndex: number) => {
        const groupIndex = item.criterios.AND.findIndex((a) => a.id === groupId)
        if (groupIndex === -1) return
        const group = item.criterios.AND[groupIndex] as CriterioOrGroup
        const leftItems = group.OR.slice(0, orIndex)
        const rightItems = group.OR.slice(orIndex)
        const newAndItems: CriterioAndItem[] =
            leftItems.length === 1
                ? [leftItems[0]]
                : [{id: group.id, OR: leftItems}]
        if (rightItems.length === 1) newAndItems.push(rightItems[0])
        else newAndItems.push({id: uuidv4(), OR: rightItems})
        updateCriterios([
            ...item.criterios.AND.slice(0, groupIndex),
            ...newAndItems,
            ...item.criterios.AND.slice(groupIndex + 1),
        ])
    }

    const toggleValue = (current: string[], valueId: string): string[] =>
        current.includes(valueId)
            ? current.filter((v) => v !== valueId)
            : [...current, valueId]

    const renderCriterioBlock = (
        block: CriterioItem,
        onQuestionChange: (uuid: string) => void,
        onValueToggle: (valueId: string) => void,
        onRemoveBlock?: () => void
    ) => {
        const selectedQuestion = questions.find(
            (q) => q.id === block.question_uuid
        )
        return (
            <CriterioBlockCard>
                <CriterioBlockHeader>
                    <BlockLabel>Condição</BlockLabel>
                    {onRemoveBlock && (
                        <IconButton size="small" onClick={onRemoveBlock}>
                            <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                    )}
                </CriterioBlockHeader>
                <Select
                    value={block.question_uuid}
                    onChange={(e) => onQuestionChange(e.target.value)}
                >
                    <option value="" disabled>
                        Selecione a pergunta
                    </option>
                    {questions.map((q) => (
                        <option key={q.id} value={q.id}>
                            {q.label}
                        </option>
                    ))}
                </Select>
                {selectedQuestion && (
                    <RadioBox>
                        {selectedQuestion.options.map((opt) => (
                            <RadioOption key={opt.id}>
                                <input
                                    type="checkbox"
                                    value={opt.id}
                                    checked={block.values.includes(opt.id)}
                                    onChange={() => onValueToggle(opt.id)}
                                />
                                {opt.label}
                            </RadioOption>
                        ))}
                    </RadioBox>
                )}
            </CriterioBlockCard>
        )
    }

    return (
        <div ref={setNodeRef} style={style}>
            <Step expanded>
                <StepLabel
                    icon={index + 1}
                    sx={{
                        '& .MuiStepLabel-label': {
                            display: 'none',
                        },
                    }}
                >
                    Minuta
                </StepLabel>
                <StepContent>
                    <Card>
                        <HeaderRow>
                            <HeaderLeft>
                                <IconButton
                                    size="small"
                                    {...attributes}
                                    {...listeners}
                                    aria-label="Mover minuta"
                                >
                                    <DragIndicatorIcon fontSize="small" />
                                </IconButton>
                                <span>{item.templateName || `Minuta ${index + 1}`}</span>
                                {selectedDocType && (
                                    <DocumentTypeBadge
                                        docType={selectedDocType}
                                        iconPath={apiDocType?.icon_path}
                                        phase={apiDocType?.phase}
                                    />
                                )}
                            </HeaderLeft>
                            <HeaderRight>
                                    {onGenerateProject && (
                                        <Tooltip
                                            title={
                                                acoesHabilitadas
                                                    ? 'Gerar projeto a partir desta minuta'
                                                    : 'Selecione o tipo de documento primeiro'
                                            }
                                        >
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    aria-label="Gerar projeto a partir da minuta"
                                                    disabled={!acoesHabilitadas || generating}
                                                    onClick={() => setGenerateOpen(true)}
                                                >
                                                    {generating ? (
                                                        <CircularProgress size={18} />
                                                    ) : (
                                                        <PlayCircleOutlineIcon fontSize="small" />
                                                    )}
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                    {onAdjustManifest && (
                                        <Tooltip
                                            title={
                                                acoesHabilitadas
                                                    ? 'Ajustar o manifesto (draft.json) via prompt'
                                                    : 'Selecione o tipo de documento primeiro'
                                            }
                                        >
                                            <span>
                                                <IconButton
                                                    size="small"
                                                    aria-label="Ajustar manifesto via prompt"
                                                    disabled={!acoesHabilitadas}
                                                    onClick={() => setAdjustOpen(true)}
                                                >
                                                    <TuneIcon fontSize="small" />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    )}
                                    <IconButton
                                        size="small"
                                        aria-label="Remover minuta"
                                        onClick={() => setConfirmDeleteOpen(true)}
                                    >
                                        <DeleteOutlineIcon fontSize="small" />
                                    </IconButton>

                                <IconButton
                                    size="small"
                                    aria-label={collapsed ? 'Expandir minuta' : 'Retrair minuta'}
                                    onClick={() => setCollapsed((prev) => !prev)}
                                >
                                    <ChevronIcon $collapsed={collapsed} fontSize="small" />
                                </IconButton>
                            </HeaderRight>
                        </HeaderRow>
                        <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
                            <DialogTitle>Excluir minuta</DialogTitle>
                            <DialogContent>
                                <DialogContentText>
                                    Tem certeza que deseja excluir a minuta{' '}
                                    <strong>{item.templateName || `Minuta ${index + 1}`}</strong>?
                                    Esta ação não pode ser desfeita.
                                </DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button onClick={() => setConfirmDeleteOpen(false)}>
                                    Cancelar
                                </Button>
                                <Button
                                    color="error"
                                    onClick={() => {
                                        setConfirmDeleteOpen(false)
                                        onRemove(item.id)
                                    }}
                                >
                                    Excluir
                                </Button>
                            </DialogActions>
                        </Dialog>
                        <Dialog
                            open={generateOpen}
                            onClose={() => !generating && setGenerateOpen(false)}
                            fullWidth
                            maxWidth="sm"
                        >
                            <DialogTitle>Gerar projeto</DialogTitle>
                            <DialogContent>
                                <DialogContentText sx={{mb: 2}}>
                                    As perguntas do fluxo são respondidas automaticamente a
                                    partir de um documento. Escolha a fonte.
                                </DialogContentText>
                                <RadioGroup
                                    value={generateMode}
                                    onChange={(e) =>
                                        setGenerateMode(e.target.value as 'draft' | 'upload')
                                    }
                                >
                                    <FormControlLabel
                                        value="draft"
                                        control={<Radio />}
                                        disabled={generating}
                                        label="Usar a minuta (draft.docx) desta configuração"
                                    />
                                    <FormControlLabel
                                        value="upload"
                                        control={<Radio />}
                                        disabled={generating}
                                        label="Enviar outro arquivo"
                                    />
                                </RadioGroup>
                                {generateMode === 'upload' && (
                                    <Box sx={{mt: 1, ml: 4}}>
                                        <Button
                                            component="label"
                                            variant="outlined"
                                            size="small"
                                            startIcon={<CloudUploadOutlinedIcon />}
                                            disabled={generating}
                                        >
                                            Escolher arquivo
                                            <input
                                                type="file"
                                                hidden
                                                accept=".docx,.pdf,.txt"
                                                onChange={(e) =>
                                                    setGenerateFile(
                                                        e.target.files?.[0] ?? null
                                                    )
                                                }
                                            />
                                        </Button>
                                        <Box
                                            component="span"
                                            sx={{ml: 2, fontSize: 13, color: '#57606a'}}
                                        >
                                            {generateFile
                                                ? generateFile.name
                                                : 'nenhum arquivo selecionado'}
                                        </Box>
                                        <DialogContentText sx={{mt: 2, fontSize: 13}}>
                                            O arquivo passa pela extração de texto antes de
                                            responder as perguntas, então a resposta não é
                                            imediata — o projeto abre e as respostas chegam
                                            em seguida.
                                        </DialogContentText>
                                    </Box>
                                )}
                                <TextField
                                    sx={{mt: 3}}
                                    fullWidth
                                    multiline
                                    minRows={2}
                                    label="Instruções para o preenchimento (opcional)"
                                    placeholder="Ex.: use o Sebrae Nacional como contratante e valores na casa de R$ 200 mil."
                                    value={generateInstruction}
                                    onChange={(e) => setGenerateInstruction(e.target.value)}
                                    disabled={generating}
                                />
                                <DialogContentText sx={{mt: 1.5, fontSize: 13}}>
                                    As perguntas que o documento não responder são preenchidas
                                    com dados plausíveis, para sair um documento completo de
                                    teste. Não use este projeto como peça real.
                                </DialogContentText>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={() => setGenerateOpen(false)}
                                    disabled={generating}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleGenerateProject}
                                    disabled={
                                        generating ||
                                        (generateMode === 'upload' && !generateFile)
                                    }
                                >
                                    {generating ? 'Gerando...' : 'Gerar projeto'}
                                </Button>
                            </DialogActions>
                        </Dialog>
                        <Dialog
                            open={adjustOpen}
                            onClose={() => !adjusting && setAdjustOpen(false)}
                            fullWidth
                            maxWidth="sm"
                        >
                            <DialogTitle>Ajustar manifesto</DialogTitle>
                            <DialogContent>
                                <DialogContentText sx={{mb: 2}}>
                                    O ajuste é aplicado sobre o manifesto atual (draft.json) da
                                    minuta <strong>{item.templateName || `Minuta ${index + 1}`}</strong>,
                                    sem reextrair da minuta — o que já está correto é preservado.
                                </DialogContentText>
                                <TextField
                                    autoFocus
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    label="O que deve mudar (opcional se enviar um documento)"
                                    placeholder="Ex.: a role SectionNumbered1 deve ter fundo BFBFBF e os subitens devem herdar o recuo da lista."
                                    value={adjustInstruction}
                                    onChange={(e) => setAdjustInstruction(e.target.value)}
                                    disabled={adjusting}
                                />
                                <Box sx={{mt: 3}}>
                                    <Button
                                        component="label"
                                        variant="outlined"
                                        size="small"
                                        startIcon={<CloudUploadOutlinedIcon />}
                                        disabled={adjusting}
                                    >
                                        Enviar documento gerado
                                        <input
                                            type="file"
                                            hidden
                                            accept=".docx"
                                            onChange={(e) =>
                                                setAdjustFile(e.target.files?.[0] ?? null)
                                            }
                                        />
                                    </Button>
                                    <Box
                                        component="span"
                                        sx={{ml: 2, fontSize: 13, color: '#57606a'}}
                                    >
                                        {adjustFile ? adjustFile.name : 'nenhum arquivo'}
                                    </Box>
                                    <DialogContentText sx={{mt: 1.5, fontSize: 13}}>
                                        Com um .docx gerado a partir desta minuta, o backend
                                        compara os dois e manda a tabela de divergências junto
                                        — só as que são responsabilidade do manifesto.
                                    </DialogContentText>
                                </Box>
                            </DialogContent>
                            <DialogActions>
                                <Button
                                    onClick={() => setAdjustOpen(false)}
                                    disabled={adjusting}
                                >
                                    Cancelar
                                </Button>
                                <Button
                                    variant="contained"
                                    onClick={handleAdjustSubmit}
                                    disabled={
                                        adjusting ||
                                        (adjustInstruction.trim().length < 3 && !adjustFile)
                                    }
                                >
                                    {adjusting ? 'Enviando...' : 'Aplicar ajuste'}
                                </Button>
                            </DialogActions>
                        </Dialog>
                        {!collapsed && (<><Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                            }}
                        >
                            <QuestionTitle>Nome do template *</QuestionTitle>
                            <UploadInput
                                as="input"
                                type="text"
                                placeholder="Ex: Contrato Padrão"
                                value={item.templateName}
                                onChange={handleTemplateNameChange}
                            />
                            <QuestionSubtitle>
                                Identificador para este template de minuta
                            </QuestionSubtitle>
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                            }}
                        >
                            <QuestionTitle>Tipo de documento *</QuestionTitle>
                            <Select
                                value={item.tipoDocumento}
                                onChange={handleTipoChange}
                            >
                                <option value="" disabled>
                                    Selecione
                                </option>
                                {documentTypes.map((docType) => (
                                    <option key={docType.id} value={docType.id}>
                                        {docType.name}
                                    </option>
                                ))}
                            </Select>
                            <QuestionSubtitle>
                                Defina o tipo de documento que será gerado a
                                partir desta minuta
                            </QuestionSubtitle>
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                            }}
                        >
                            <input
                                type="checkbox"
                                id={`force-extraction-${item.id}`}
                                checked={item.forceExtraction}
                                onChange={(e) =>
                                    onUpdate(item.id, {forceExtraction: e.target.checked})
                                }
                            />
                            <QuestionTitle htmlFor={`force-extraction-${item.id}`}>
                                Forçar extração do manifesto
                            </QuestionTitle>
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                            }}
                        >
                            <QuestionTitle>Minuta *</QuestionTitle>
                            {item.minutaFileName ? (
                                <SelectedFile>
                                    <SelectedFileInfo>
                                        <DescriptionOutlinedIcon fontSize="small" />
                                        {item.minutaFileName}
                                    </SelectedFileInfo>
                                    <RemoveFileButton
                                        type="button"
                                        onClick={() =>
                                            onMinutaFileRemove(item.id)
                                        }
                                        aria-label="Remover arquivo"
                                    >
                                        x
                                    </RemoveFileButton>
                                </SelectedFile>
                            ) : (
                                <UploadInput
                                    key={item.minutaFileKey}
                                    type="file"
                                    accept=".docx"
                                    onChange={handleMinutaFileChange}
                                />
                            )}
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                            }}
                        >
                            <QuestionTitle>Conteudo da Minuta (opcional)</QuestionTitle>
                            {item.minutaContentFileName ? (
                                <SelectedFile>
                                    <SelectedFileInfo>
                                        <DescriptionOutlinedIcon fontSize="small" />
                                        {item.minutaContentFileName}
                                    </SelectedFileInfo>
                                    <RemoveFileButton
                                        type="button"
                                        onClick={() =>
                                            onTemplateFileRemove(item.id)
                                        }
                                        aria-label="Remover arquivo"
                                    >
                                        x
                                    </RemoveFileButton>
                                </SelectedFile>
                            ) : (
                                <UploadInput
                                    key={item.minutaContentFileKey}
                                    type="file"
                                    accept=".txt"
                                    onChange={handleTemplateFileChange}
                                />
                            )}
                        </Box>
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                            }}
                        >
                            <QuestionTitle>Prompt da IA</QuestionTitle>
                            <TextArea
                                placeholder="Com base na minuta enviada, gere o documento final seguindo fielmente o estilo, formatação e estrutura da minuta. Adapte o conteúdo conforme as informações do cliente..."
                                value={item.prompt}
                                onChange={handlePromptChange}
                            />
                            <QuestionSubtitle>
                                Defina instruções adicionais para a geração da
                                minuta
                            </QuestionSubtitle>
                        </Box>

                        <Divider />

                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                            }}
                        >
                            <QuestionTitle>Critério de seleção</QuestionTitle>
                            <QuestionSubtitle>
                                Defina quando esta minuta deve ser utilizada com
                                base nas respostas do formulário
                            </QuestionSubtitle>

                            <CriterioWrapper>
                                {item.criterios.AND.map((andItem, andIndex) => (
                                    <React.Fragment key={andItem.id}>
                                        {andIndex > 0 && (
                                            <ConnectorRow>
                                                <ConnectorLine />
                                                <ConnectorPill
                                                    variant="and"
                                                    clickable={
                                                        !isOrGroup(item.criterios.AND[andIndex - 1]) &&
                                                        !isOrGroup(andItem)
                                                    }
                                                    onClick={() =>
                                                        handleToggleAndToOr(andIndex)
                                                    }
                                                >
                                                    E
                                                </ConnectorPill>
                                                <ConnectorLine />
                                            </ConnectorRow>
                                        )}

                                        {isOrGroup(andItem) ? (
                                            <OrGroupCard>
                                                <OrGroupHeader>
                                                    <ConnectorPill variant="or">
                                                        OU
                                                    </ConnectorPill>
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            handleRemoveAndItem(
                                                                andItem.id
                                                            )
                                                        }
                                                    >
                                                        <DeleteOutlineIcon fontSize="small" />
                                                    </IconButton>
                                                </OrGroupHeader>

                                                {andItem.OR.map(
                                                    (orBlock, orIndex) => (
                                                        <React.Fragment
                                                            key={orBlock.id}
                                                        >
                                                            {orIndex > 0 && (
                                                                <ConnectorRow>
                                                                    <ConnectorLine />
                                                                    <ConnectorPill
                                                                        variant="or"
                                                                        clickable
                                                                        onClick={() =>
                                                                            handleToggleOrToAnd(
                                                                                andItem.id,
                                                                                orIndex
                                                                            )
                                                                        }
                                                                    >
                                                                        OU
                                                                    </ConnectorPill>
                                                                    <ConnectorLine />
                                                                </ConnectorRow>
                                                            )}
                                                            {renderCriterioBlock(
                                                                orBlock,
                                                                (uuid) =>
                                                                    handleUpdateOrBlock(
                                                                        andItem.id,
                                                                        orBlock.id,
                                                                        {
                                                                            question_uuid: uuid,
                                                                            values: [],
                                                                        }
                                                                    ),
                                                                (valueId) =>
                                                                    handleUpdateOrBlock(
                                                                        andItem.id,
                                                                        orBlock.id,
                                                                        {
                                                                            values: toggleValue(
                                                                                orBlock.values,
                                                                                valueId
                                                                            ),
                                                                        }
                                                                    ),
                                                                andItem.OR
                                                                    .length > 1
                                                                    ? () =>
                                                                          handleRemoveOrBlock(
                                                                              andItem.id,
                                                                              orBlock.id
                                                                          )
                                                                    : undefined
                                                            )}
                                                        </React.Fragment>
                                                    )
                                                )}

                                                <Button
                                                    size="small"
                                                    variant="text"
                                                    onClick={() =>
                                                        handleAddOrBlock(
                                                            andItem.id
                                                        )
                                                    }
                                                    sx={{
                                                        alignSelf: 'flex-start',
                                                        mt: 1,
                                                        color: '#0969da',
                                                        fontSize: '12px',
                                                    }}
                                                >
                                                    + Adicionar OU
                                                </Button>
                                            </OrGroupCard>
                                        ) : (
                                            renderCriterioBlock(
                                                andItem,
                                                (uuid) =>
                                                    handleUpdateAndBlock(
                                                        andItem.id,
                                                        {question_uuid: uuid, values: []}
                                                    ),
                                                (valueId) =>
                                                    handleUpdateAndBlock(
                                                        andItem.id,
                                                        {
                                                            values: toggleValue(
                                                                andItem.values,
                                                                valueId
                                                            ),
                                                        }
                                                    ),
                                                item.criterios.AND.length > 1
                                                    ? () =>
                                                          handleRemoveAndItem(
                                                              andItem.id
                                                          )
                                                    : undefined
                                            )
                                        )}
                                    </React.Fragment>
                                ))}

                                <CriterioActions>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={handleAddAndBlock}
                                        sx={{
                                            borderStyle: 'dashed',
                                            borderColor: '#57606a',
                                            color: '#57606a',
                                            fontSize: '12px',
                                        }}
                                    >
                                        + Adicionar condição
                                    </Button>
                                    <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={handleAddOrGroup}
                                        sx={{
                                            borderStyle: 'dashed',
                                            borderColor: '#54aeff',
                                            color: '#0969da',
                                            fontSize: '12px',
                                        }}
                                    >
                                        + Adicionar grupo OU
                                    </Button>
                                </CriterioActions>
                            </CriterioWrapper>
                        </Box>

                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '8px',
                            }}
                        >
                            <QuestionTitle>Timbrado da Minuta</QuestionTitle>
                            <QuestionSubtitle>
                                Papel timbrado opcional para documentos que não
                                se enquandram no timbrado do padrão
                            </QuestionSubtitle>
                            {item.timbradoFileName ? (
                                <SelectedFile>
                                    <SelectedFileInfo>
                                        <DescriptionOutlinedIcon fontSize="small" />
                                        {item.timbradoFileName}
                                    </SelectedFileInfo>
                                    <RemoveFileButton
                                        type="button"
                                        onClick={() =>
                                            onTimbradoFileRemove(item.id)
                                        }
                                        aria-label="Remover arquivo"
                                    >
                                        x
                                    </RemoveFileButton>
                                </SelectedFile>
                            ) : (
                                <DropZone
                                    onDragOver={(event) =>
                                        event.preventDefault()
                                    }
                                    onDrop={handleTimbradoDrop}
                                >
                                    <CloudUploadOutlinedIcon />
                                    <DropZoneText>
                                        Arraste ou clique para fazer upload
                                    </DropZoneText>
                                    <DropZoneHint>
                                        .docx · máx. 10MB
                                    </DropZoneHint>
                                    <HiddenInput
                                        key={item.timbradoFileKey}
                                        type="file"
                                        accept=".docx"
                                        onChange={handleTimbradoFileChange}
                                    />
                                </DropZone>
                            )}
                        </Box>
                        </>)}
                    </Card>
                </StepContent>
            </Step>
        </div>
    )
}
